import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from '@/components/ConfirmProvider';
import { Badge, Button, Card } from '@/components/ui';
import { api } from '@/lib/api/client';
import type { ApiException } from '@/lib/api/types';
import { formatDateTime } from '@/lib/format';
import { AI_REVIEW_MARK } from './aiReviewMark';

/**
 * 🔎 AI 2차 검토 호출 켜고 끄기 — 관리자 › AI 비용 탭 (2026-10-19 · 추천보고서 세션 백엔드 V158 td_ai_review_config · 5e 버튼)
 *
 * 왜 — 전에는 trading.yml(ai-review.llm-enabled)만 있어 끄려면 배포가 필요했다. 이제 관리자가 화면에서 바로 끄고 켠다.
 *   설정 파일 값은 "기본값" 으로 남고, 화면에서 정한 값(override)이 있으면 그것이 이긴다. [기본으로]는 덮어쓰기를 지운다.
 * 끄면 — 새 신호는 AI 없이 사실만 남고(FACTS_ONLY), 이미 대기 중인 건은 그대로 있다가 다시 켜면 처리된다(백엔드 AiReviewSwitch).
 * 화면 설정(저점 신호 검토 · 스윙 도움말의 llmEnabled)도 이 값을 따른다 — 그래서 바꾸면 그 조회도 새로 받는다.
 */

/** GET · PUT /api/admin/trading/ai-review/config 의 data (AiReviewSwitch.State) */
interface AiReviewSwitchState {
  /** 지금 실제로 쓰는 값 */
  llmEnabled: boolean;
  /** 화면에서 정한 값 · null 이면 설정 파일 기본을 따른다 */
  override: boolean | null;
  /** 설정 파일(trading.yml) 기본값 */
  ymlDefault: boolean;
  updatedAt: string | null;
  /** 바꾼 회원 id */
  updatedBy: number | null;
}

const PATH = '/admin/trading/ai-review/config';
const QUERY_KEY = ['admin', 'ai-review-switch'];

function onOff(on: boolean): string {
  return on ? '켜짐' : '꺼짐';
}

export function AiReviewSwitchCard() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const state = useQuery<AiReviewSwitchState, ApiException>({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<AiReviewSwitchState>(PATH),
  });
  const change = useMutation<AiReviewSwitchState, ApiException, boolean | null>({
    mutationFn: (llmEnabled) => api.put<AiReviewSwitchState>(PATH, { llmEnabled }),
    onSuccess: (next) => {
      queryClient.setQueryData(QUERY_KEY, next);
      // 저점 신호 검토 화면 · 스윙 감시 표 칩이 읽는 설정(llmEnabled)도 새로 받는다(useAiReview 의 키 머리)
      void queryClient.invalidateQueries({ queryKey: ['trading', 'ai-review'] });
    },
  });

  const s = state.data;

  async function turnOff() {
    const ok = await confirm({
      title: 'AI 2차 검토 호출을 끌까요?',
      // 2026-10-19 화면 점검 — 대기 건은 다시 켠 "순간" 이 아니라 그 뒤 다음 감시 때 집는다(AiReviewDrainer) · 말투는 카드 본문(합니다체)에 맞춘다
      description: '끄면 새 신호에 AI 보고서가 붙지 않습니다. 이미 대기 중인 건은 그대로 두었다가, 다시 켜면 다음 감시 때 하루 상한 안에서 처리됩니다.',
      confirmText: '끄기',
      tone: 'danger',
    });
    if (ok) {
      change.mutate(false);
    }
  }

  return (
    <Card title={`${AI_REVIEW_MARK} AI 2차 검토 호출`}>
      {state.isLoading && <p className="text-sm text-fg-muted">불러오는 중입니다.</p>}
      {state.error && <p className="text-sm text-danger">{state.error.message}</p>}
      {s && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={s.llmEnabled ? 'success' : 'warning'}>{onOff(s.llmEnabled)}</Badge>
            <span className="text-fg-muted">
              설정 파일 기본: {onOff(s.ymlDefault)}
              {s.override === null ? ' · 지금 기본을 따름' : ' · 화면에서 정한 값'}
            </span>
          </div>
          {s.updatedAt && (
            <p className="mt-1 text-xs text-fg-subtle">
              마지막 변경 {formatDateTime(s.updatedAt)}
              {s.updatedBy !== null ? ` · 회원 ${s.updatedBy}` : ''}
            </p>
          )}
          <p className="mt-2 text-xs text-fg-muted">
            스윙(6개월) 감시 종목이 1단선에 닿을 때 AI 보고서를 쓸지 정합니다. 꺼도 공시 · 기사 · 숫자 기록은 그대로 남습니다.
            [기본으로]는 화면에서 정한 값을 지우고 설정 파일 값을 따릅니다.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={change.isPending || s.override === true}
              onClick={() => change.mutate(true)}
            >
              켜기
            </Button>
            <Button variant="danger" disabled={change.isPending || s.override === false} onClick={() => void turnOff()}>
              끄기
            </Button>
            <Button variant="ghost" disabled={change.isPending || s.override === null} onClick={() => change.mutate(null)}>
              기본으로
            </Button>
          </div>
        </>
      )}
      {change.error && <p className="mt-2 text-sm text-danger">{change.error.message}</p>}
    </Card>
  );
}
